import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

import supabase from "@/services/supabaseClient";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import SaveChip from "@/sot/primitives/SaveChip.jsx";
import Snackbar from "@/sot/primitives/Snackbar.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import { loadPatientIntroPreview, savePatientIntroPreview } from "@/lib/patientIntroPreview";
import { PATIENT_SETTINGS_PROFILE_ROUTE } from "@/pages/profile-settings/settingsNavigation.js";
import FolaOnboardingCard from "@/pages/profile-settings/FolaOnboardingCard.jsx";

function normalizeMetric(value) {
  const text = String(value ?? "").trim();
  if (!text) return "";
  const numeric = Number(text.replace(",", "."));
  if (!Number.isFinite(numeric) || numeric < 0) return "";
  return String(numeric);
}

function isFrenchLanguageCode(languageCode) {
  return String(languageCode || "").toLowerCase().startsWith("fr");
}

export default function HeightWeightSettings() {
  const nav = useNavigate();
  const location = useLocation();
  const { refreshProfile } = useAuth() || {};
  const { t, languageCode } = useLanguage();
  const isFrench = isFrenchLanguageCode(languageCode);
  const returnTo = String(location.state?.returnTo || "").trim();

  const preview = React.useMemo(() => loadPatientIntroPreview() || {}, []);
  const initialHeight = normalizeMetric(preview.heightCm || preview.height_cm);
  const initialWeight = normalizeMetric(preview.weightKg || preview.weight_kg);

  const [seedHeight, setSeedHeight] = React.useState(initialHeight);
  const [seedWeight, setSeedWeight] = React.useState(initialWeight);
  const [height, setHeight] = React.useState(initialHeight);
  const [weight, setWeight] = React.useState(initialWeight);
  const [saved, setSaved] = React.useState(false);
  const [saveError, setSaveError] = React.useState("");

  React.useEffect(() => {
    let alive = true;

    async function hydrate() {
      const {
        data: { user },
        error: authErr,
      } = await supabase.auth.getUser();
      if (!alive || authErr || !user?.id) return;

      const { data: ehrRow } = await supabase
        .from("ehr_patient_info")
        .select("height_cm,weight_kg")
        .eq("patient_id", user.id)
        .maybeSingle();

      const nextHeight = normalizeMetric(ehrRow?.height_cm);
      const nextWeight = normalizeMetric(ehrRow?.weight_kg);
      if (!alive) return;

      const displayHeight = initialHeight || nextHeight;
      const displayWeight = initialWeight || nextWeight;

      setSeedHeight(nextHeight);
      setSeedWeight(nextWeight);
      setHeight(displayHeight);
      setWeight(displayWeight);
      savePatientIntroPreview({
        ...(loadPatientIntroPreview() || {}),
        heightCm: displayHeight,
        weightKg: displayWeight,
      });
    }

    hydrate();
    return () => {
      alive = false;
    };
  }, [initialHeight, initialWeight]);

  const dirty = height !== seedHeight || weight !== seedWeight;

  async function handleSave() {
    if (!dirty) return;

    try {
      const nextHeight = normalizeMetric(height);
      const nextWeight = normalizeMetric(weight);

      savePatientIntroPreview({
        ...(loadPatientIntroPreview() || {}),
        heightCm: nextHeight,
        weightKg: nextWeight,
      });

      const {
        data: { user },
        error: authErr,
      } = await supabase.auth.getUser();
      if (authErr) throw authErr;

      if (user?.id) {
        const { error: ehrErr } = await supabase.from("ehr_patient_info").upsert(
          {
            patient_id: user.id,
            height_cm: nextHeight ? Number(nextHeight) : null,
            weight_kg: nextWeight ? Number(nextWeight) : null,
          },
          { onConflict: "patient_id" }
        );
        if (ehrErr) throw ehrErr;

        try {
          await refreshProfile?.();
        } catch {
          // non-fatal
        }
      }

      setSeedHeight(nextHeight);
      setSeedWeight(nextWeight);
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        if (returnTo) {
          nav(returnTo, { replace: true });
        } else {
          nav(PATIENT_SETTINGS_PROFILE_ROUTE);
        }
      }, 2200);
    } catch (err) {
      console.error("[HeightWeightSettings] Save error:", err);
      setSaveError(
        t(
          "settings.subflows.heightWeight.saveError",
          isFrench ? "Impossible d’enregistrer la taille et le poids. Veuillez réessayer." : "Could not save height and weight. Please try again."
        )
      );
    }
  }

  return (
    <SettingsScreenShell>
      <TopBarCenter
        title={t("settings.subflows.heightWeight.title", isFrench ? "Taille et poids" : "Height & Weight")}
        onBack={() => {
        if (returnTo) {
          nav(returnTo, { replace: true });
        } else {
          nav(PATIENT_SETTINGS_PROFILE_ROUTE);
        }
      }}
        close
        right={<SaveChip disabled={!dirty} onClick={handleSave} />}
      />

      <div className="mt-3">
        <FolaOnboardingCard role="patient" />
      </div>

      <p className="mt-1 mb-4 text-body-medium text-on-surface-var">
        {t(
          "settings.subflows.heightWeight.subtitle",
          isFrench
            ? "Ajoutez votre taille et votre poids les plus récents afin que votre profil médical reste utile pour les soins."
            : "Add your latest height and weight so your medical profile stays more useful for care."
        )}
      </p>
      <div className="mt-6 space-y-6">
        <div className="relative">
          <TextField56
            name="heightCm"
            label={t("settings.subflows.heightWeight.heightLabel", isFrench ? "Taille (cm)" : "Height (cm)")}
            value={height}
            onChange={(value) => setHeight(typeof value === "string" ? value : value?.target?.value ?? "")}
            placeholder={t("settings.subflows.heightWeight.heightPlaceholder", "175")}
            inputMode="decimal"
            tone="surface"
            clearable={false}
          />
          {height ? (
            <button
              type="button"
              aria-label={t("settings.subflows.heightWeight.clearHeight", isFrench ? "Effacer la taille" : "Clear height")}
              onClick={() => setHeight("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 grid place-items-center"
              style={{ width: 44, height: 44, borderRadius: 8, WebkitTapHighlightColor: "transparent" }}
            >
              <img src="/icons/doctor/cancel.svg" alt="" className="h-5 w-5" />
            </button>
          ) : null}
        </div>

        <div className="relative">
          <TextField56
            name="weightKg"
            label={t("settings.subflows.heightWeight.weightLabel", isFrench ? "Poids (kg)" : "Weight (kg)")}
            value={weight}
            onChange={(value) => setWeight(typeof value === "string" ? value : value?.target?.value ?? "")}
            placeholder={t("settings.subflows.heightWeight.weightPlaceholder", "72")}
            inputMode="decimal"
            tone="surface"
            clearable={false}
          />
          {weight ? (
            <button
              type="button"
              aria-label={t("settings.subflows.heightWeight.clearWeight", isFrench ? "Effacer le poids" : "Clear weight")}
              onClick={() => setWeight("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 grid place-items-center"
              style={{ width: 44, height: 44, borderRadius: 8, WebkitTapHighlightColor: "transparent" }}
            >
              <img src="/icons/doctor/cancel.svg" alt="" className="h-5 w-5" />
            </button>
          ) : null}
        </div>
      </div>

      <Snackbar
        open={saved}
        onClose={() => setSaved(false)}
        duration={2200}
      >
        {t("common.changesSaved", isFrench ? "Modifications enregistrées" : "Changes saved")}
      </Snackbar>
      {saveError ? (
        <Snackbar
          open={!!saveError}
          onClose={() => setSaveError("")}
        >
          {saveError || t("settings.subflows.heightWeight.saveError", isFrench ? "Impossible d’enregistrer la taille et le poids. Veuillez réessayer." : "Could not save height and weight. Please try again.")}
        </Snackbar>
      ) : null}
    </SettingsScreenShell>
  );
}
